import { getFirestore, doc, onSnapshot } from "firebase/firestore";
import { Play } from "lucide-react";
import { useState, useEffect, useId } from "react";
import { useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { PageHeader, SectionHeader } from "@/components/ui/page-header";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { Spinner } from "@/components/ui/spinner";
import { Textarea } from "@/components/ui/textarea";
import { getSupportScripts, runSupportScript } from "../api/OpenAIApi";

export const SupportScripts = () => {
  type SupportScriptParam = {
    description: string;
    is_required: boolean;
    name: string;
    type: string;
    default?: string;
  };

  type SupportScript = {
    display_name: string;
    description: string;
    name: string;
    params: SupportScriptParam[];
  };

  const [searchParams] = useSearchParams();
  const scriptSelectId = useId();

  const [supportScripts, setSupportScripts] = useState<SupportScript[]>([]);
  const [selectedScript, setSelectedScript] = useState<SupportScript>();

  const [paramValues, setParamValues] = useState<Record<string, any>>({});
  const [result, setResult] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  const [status, setStatus] = useState<string | null>(null);
  const [output, setOutput] = useState<any>(null);

  const db = getFirestore();

  useEffect(() => {
    const fetchSupportScripts = async () => {
      const scripts = await getSupportScripts();
      setSupportScripts(scripts);

      const scriptNameFromQuery = searchParams.get("name");
      if (scriptNameFromQuery) {
        const matchingScript: SupportScript = scripts.find(
          (script: SupportScript) => script.name === scriptNameFromQuery
        );

        if (matchingScript) {
          setSelectedScript(matchingScript);

          // Build default + query override param values
          const defaults: Record<string, any> = {};
          matchingScript.params.forEach((param) => {
            const queryValue = searchParams.get(param.name);
            if (queryValue !== null) {
              defaults[param.name] =
                param.type === "bool"
                  ? queryValue === "true"
                  : decodeURIComponent(queryValue);
            } else {
              defaults[param.name] = param.default || "";
            }
          });

          setParamValues(defaults);
        }
      }
    };

    fetchSupportScripts();
  }, [db, searchParams]);

  const handleSubmit = async () => {
    if (!selectedScript) return;
    setLoading(true);
    setResult(null);

    try {
      const res = await runSupportScript(selectedScript.name, paramValues);
      setResult(res.run_doc_id);
    } catch (err) {
      setResult({ err });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!result || typeof result !== "string") {
      return undefined; // explicitly return `undefined` to satisfy ESLint
    }

    const runDocRef = doc(db, "support_script_runs", result);

    const unsubscribe = onSnapshot(runDocRef, (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        setStatus(data.status || null);
        setOutput(data.output || null);
      }
    });

    return () => unsubscribe();
  }, [result]);

  const handleScriptChange = (scriptName: string) => {
    const script = supportScripts.find((s) => s.name === scriptName);
    setSelectedScript(script);

    const defaults: Record<string, any> = {};
    script?.params.forEach((param) => {
      defaults[param.name] = param.default || "";
    });

    setParamValues(defaults);
    setResult(null);
    setStatus(null);
    setOutput(null);
  };

  const handleInputChange = (name: any, value: string | boolean) => {
    setParamValues((prev) => ({ ...prev, [name]: value }));
  };

  const isRunning = () => {
    return loading || (status && status === "pending") || status === "running";
  };

  const running = Boolean(isRunning());

  return (
    <div className="h-full overflow-y-auto bg-surface">
      <div className="mx-auto flex w-full max-w-190 flex-col gap-8 px-12 pt-10 pb-24">
        <PageHeader
          title="Support Scripts"
          description="Select a support script to run."
        />

        <section className="flex flex-col gap-3">
          <SectionHeader title="Script" />
          <div className="flex flex-col gap-4 rounded-lg bg-elevated p-4 ring-1 ring-line-2">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor={scriptSelectId}>Select Support Script</Label>
              <Select
                value={selectedScript?.name}
                onValueChange={handleScriptChange}
              >
                <SelectTrigger id={scriptSelectId} className="w-full">
                  <SelectValue placeholder="Choose a script to run..." />
                </SelectTrigger>
                <SelectContent>
                  {supportScripts.map((script) => (
                    <SelectItem key={script.name} value={script.name}>
                      {script.display_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {selectedScript && (
              <p className="text-base text-fg-2">
                {selectedScript.description}
              </p>
            )}
          </div>
        </section>

        {selectedScript && (
          <section className="flex flex-col gap-3">
            <SectionHeader
              title="Parameters"
              actions={
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleSubmit}
                  disabled={running}
                >
                  {running ? <Spinner /> : <Play />}
                  {running ? "Running..." : "Run Script"}
                </Button>
              }
            />
            {selectedScript.params.length > 0 && (
              <div className="flex flex-col gap-4 rounded-lg bg-elevated p-4 ring-1 ring-line-2">
                {selectedScript.params.map((param) => (
                  <div key={param.name} className="flex flex-col gap-1.5">
                    <Label htmlFor={param.name}>
                      {param.name}
                      <span className="font-normal text-fg-3">
                        ({param.description})
                      </span>
                    </Label>

                    {param.type === "string" && (
                      <Textarea
                        id={param.name}
                        value={paramValues[param.name]}
                        onChange={(e) =>
                          handleInputChange(param.name, e.target.value)
                        }
                      />
                    )}
                    {param.type === "bool" && (
                      <Select
                        value={
                          paramValues[param.name] === true ||
                          paramValues[param.name] === "true"
                            ? "true"
                            : paramValues[param.name] === false ||
                              paramValues[param.name] === "false"
                            ? "false"
                            : undefined
                        }
                        onValueChange={(v) =>
                          handleInputChange(param.name, v === "true")
                        }
                      >
                        <SelectTrigger id={param.name} className="w-full">
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="true">True</SelectItem>
                          <SelectItem value="false">False</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                    {/* Add more type handlers here if needed */}
                  </div>
                ))}
              </div>
            )}
          </section>
        )}

        {status && (
          <section className="flex flex-col gap-3">
            <SectionHeader title="Output" description={`Status: ${status}`} />
            <pre className="rounded-lg bg-elevated p-4 font-mono text-sm whitespace-pre-wrap text-fg ring-1 ring-line-2">
              {Array.isArray(output)
                ? output.map((line, idx) => <div key={idx}>{line}</div>)
                : typeof output === "object"
                ? JSON.stringify(output, null, 2)
                : output}
            </pre>
          </section>
        )}
      </div>
    </div>
  );
};
